import { useState } from "react"
import { useSelector, useDispatch } from "react-redux"
import { useLocation, useNavigate } from "react-router"

import "./index.scss"

import { ConfigContainer } from "components"
import Button from "react-bootstrap/Button"
import ToggleButton from "react-bootstrap/ToggleButton"
import ToggleButtonGroup from "react-bootstrap/ToggleButtonGroup"
import EmailChangeSection from "./EmailChangeSection"
import { Duration } from "luxon"
import { Title, Container, Text, DurationInput, Select } from "components/Form"
import { members, message, analyses, seatInfo, customerAdministration, auth, limsCapabilities } from "state_management"
import ESignatureModal from "components/Lims/ESignatureModal"
import useT from "utils/hooks/useT"

const PLANNERTECH_LANGUAGE_CODES = ["en", "da", "fr", "it", "es", "pl", "de", "el", "pt", "cs", "ko"]
const PLANNERTECH_LANGUAGE_OPTIONS = [
  { id: "cs", name: "Čeština" },
  { id: "da", name: "Dansk" },
  { id: "de", name: "Deutsch" },
  { id: "el", name: "Ελληνικά" },
  { id: "en", name: "English" },
  { id: "es", name: "Español" },
  { id: "fr", name: "Français" },
  { id: "it", name: "Italiano" },
  { id: "ko", name: "한국어" },
  { id: "pl", name: "Polski" },
  { id: "pt", name: "Português" },
]

// Whether the general (planning) fields are read-only for this viewer/target pair. Pulled out of
// the component body on purpose: it keeps the branching out of an already complex render function,
// and it names the three separate reasons a field can be locked instead of hiding them in one
// boolean chain.
function generalFieldsReadOnly(member, authState) {
  if (!member) {
    return false // creating a new member — nothing is locked
  }
  if (member.archived) {
    return true
  }
  // #4123: a view-only user may now open their own settings, but may change nothing here. The
  // server refuses every write from them except their own credentials, so leaving these editable
  // would only produce a Save that fails.
  if (authState.isViewOnly) {
    return true
  }
  // A plain member may look at a colleague's row but not edit it.
  return authState.isMember && authState.memberId !== member.id
}

// Member config.
// Used both by ADMINs, MEMBERs and PLANNERs, so make sure it keeps working
// regardless of which membertype the currently logged in user is!
export default function MemberConfig({ member = null, adminTeamId = null, onClose }) {
  const authState = useSelector((state) => state.auth)
  const dispatch = useDispatch()
  const location = useLocation()
  const navigate = useNavigate()
  const { t } = useT("common")

  const [role, setRole] = useState(member ? member.role : "MEMBER")
  const [startTime, setStartTime] = useState(
    member ? Duration.fromObject({ seconds: member.workHours.min }) : Duration.fromObject({ hours: 8 })
  )
  const [endTime, setEndTime] = useState(
    member ? Duration.fromObject({ seconds: member.workHours.max }) : Duration.fromObject({ hours: 16 })
  )
  const isCustomerAdmin = useSelector((state) => state.auth.role) === "customer_admin"
  const onlyLibrarian = useSelector((state) => state.teamStatus.onlyLibrarian)

  const [email, setEmail] = useState(member ? member.email : "")
  // SPEC-057 L2: a password is only ever typed here by the member THEMSELVES (self-service
  // change, current password required). New members are created WITHOUT a password — they pick
  // their own via the welcome mail — and helping a colleague is the "send reset mail" button.
  const [password, setPassword] = useState(null)
  const [currentPassword, setCurrentPassword] = useState("")
  const [initials, setInitials] = useState(member ? member.initials : "")
  const [showOnPlan, setShowOnPlan] = useState(member ? member.showOnPlan : true)
  const [outlookLink, setOutlookLink] = useState(member ? member.outlookLink : null)
  const [plannertechLanguage, setPlannertechLanguage] = useState(() => {
    if (!member) {
      return ""
    }
    return PLANNERTECH_LANGUAGE_CODES.includes(member.plannertechLanguage) ? member.plannertechLanguage : "en"
  })

  // LIMS QC capabilities (Analyst/Reviewer/Supervisor/QA) — shown only when the LIMS module is
  // on, for an EXISTING member (capabilities are granted via the e-signed
  // POST /lims/qc-capabilities after creation — never on the create payload). SPEC-021 (B3):
  // the current capabilities are read off the member dump (member.qcCapabilities).
  // SPEC-057 L1 (two-column model): the selector offers ONLY the capabilities the CURRENT user
  // may change on this target — PLANNER: analyst/reviewer/supervisor; qa-manager holder: qa
  // (never on themselves). `qa-manager` itself is appointed by the customer admin (L3) and is
  // never offered here; capabilities the actor may not change render as locked chips that the
  // onChange guard keeps in the echoed set (the backend allows unchanged echoes as no-ops).
  const showLims = useSelector((state) => state.teamStatus.showLims)
  const canAdministerCapabilities = useSelector(limsCapabilities.selectCanAdministerCapabilities)
  const isQaManagerHolder = useSelector((state) =>
    limsCapabilities.hasQcCapability(state, state.auth.memberId, limsCapabilities.QA_MANAGER)
  )
  const [qcCapabilities, setQcCapabilities] = useState(() => (member && member.qcCapabilities) || [])
  const [initialCapabilities, setInitialCapabilities] = useState(() => (member && member.qcCapabilities) || [])
  const [esigOpen, setEsigOpen] = useState(false)
  // #4125: the "change e-mail" link sits on the email field's own row, so the panel it opens is
  // a sibling of that row — the open state has to live here rather than inside the panel.
  const [emailChangeOpen, setEmailChangeOpen] = useState(false)
  const [pendingRes, setPendingRes] = useState(null)
  // A change to QC capabilities is an authorisation change → requires an e-signature.
  const capabilitiesChanged = () =>
    [...qcCapabilities].sort().join(",") !== [...initialCapabilities].sort().join(",")
  const assignableCapabilities = limsCapabilities.assignableCapabilities({
    isPlanner: authState.role === "planner",
    isQaManagerHolder,
    isSelf: !!member && member.id === authState.memberId,
  })
  // Ordered to mirror the QC pipeline (SPEC-032 / F0); `supervisor` is the chemist lab-review role.
  const qcCapabilityOptions = [
    { id: "analyst", name: t("limsCapAnalyst") },
    { id: "reviewer", name: t("limsCapReviewer") },
    { id: "supervisor", name: t("limsCapSupervisor") },
    { id: "qa", name: t("limsCapQa") },
    { id: "qa-manager", name: t("limsCapQaManager") },
  ].filter((option) => assignableCapabilities.includes(option.id) || qcCapabilities.includes(option.id))
  // Guard: additions outside the actor's allow-set are dropped and removals of locked
  // capabilities are re-added, so the echoed set only ever differs inside the allow-set.
  const handleCapabilityChange = (selected) => {
    const next = new Set(selected.filter((c) => assignableCapabilities.includes(c) || qcCapabilities.includes(c)))
    qcCapabilities.forEach((c) => {
      if (!assignableCapabilities.includes(c)) {
        next.add(c)
      }
    })
    setQcCapabilities(limsCapabilities.QC_CAPABILITIES.filter((c) => next.has(c)))
  }
  const showCapabilitySection = showLims && !!member && canAdministerCapabilities

  // #4124: push a role change on your OWN member into the auth slice, and step off a screen the
  // new role has lost. Deliberately reads the SERVER's answer rather than the form: the backend
  // silently refuses a planner demoting themselves while they are the team's last planner, so the
  // form would otherwise display a change that never happened.
  const applyOwnRoleChange = (updated) => {
    if (!updated || !member || member.id !== authState.memberId || updated.role === member.role) {
      return
    }
    dispatch(auth.setRole(updated.role))
    // The settings screen renders almost nothing for a member, so staying there looks broken and
    // every action from it is refused. SetupScreen has its own redirect, but it only fires for one
    // team configuration — this covers the rest.
    if (updated.role !== "PLANNER" && location.pathname.startsWith("/setup")) {
      navigate("/")
    }
  }

  // Performs the actual member edit-save. Called directly when no e-signature is needed,
  // or from the e-signature modal's onSign once a QC-role change has been signed — so
  // nothing is committed (and no success shown) until the signature is completed.
  // `capabilitySignature` is the full ESignatureModal payload: setForMember lifts its
  // password onto the signed POST /lims/qc-capabilities (verified + discarded server-side).
  const runEditSave = (res, capabilitySignature) => {
    // SPEC-057 L1: a qa-manager holder with the MEMBER planning role opens this form to change
    // QC capabilities only — every general field is read-only for them (`disabled`), and the
    // member PATCH / credential write would be refused server-side. Skip the general edit
    // entirely in that case and run just the signed capability step.
    const generalEditPromise = disabled
      ? Promise.resolve()
      : (() => {
          const wasShownOnPlan = member.showOnPlan
          return dispatch(members.edit(member.id, res))
            .then((updated) => {
              if (wasShownOnPlan && !res.showOnPlan) {
                dispatch(analyses.fetchPlanned())
                dispatch(analyses.fetchUnplanned())
              }
              if (member.id === authState.memberId && res.plannertechLanguage) {
                dispatch(auth.setPlannertechLanguage(res.plannertechLanguage))
              }
              applyOwnRoleChange(updated)
            })
        })()
    return generalEditPromise
      .then(() => {
        if (showLims && capabilitiesChanged()) {
          return dispatch(limsCapabilities.setForMember(member.id, qcCapabilities, capabilitySignature))
        }
      })
      .then(() => {
        // SPEC-057 L2 + UI-review: the self-service password change (current password re-auth)
        // runs LAST, so every other field is already saved and a wrong current password only
        // costs a retry. A SUCCESSFUL change revokes every session server-side — end this one
        // deliberately with a proper explanation instead of letting the next request 401 with
        // the misleading "logged out by another device".
        if (canEditOwnCredentials && password) {
          return dispatch(members.updateCredentials(member.id, { currentPassword, newPassword: password })).then(
            () => {
              dispatch(message.info(t("passwordChangedLoggedOut")))
              dispatch(auth.endSession())
            }
          )
        }
      })
  }


  const days = ["monday", "tuesday", "wednesday", "thursday", "friday", "saturday", "sunday"]

  const renderDays = (disabled) => {
    return days.map((day) => {
      return (
        <ToggleButton
          disabled={disabled}
          className="Memberconfig-checkboxButton"
          value={day}
          title={day}
          variant="light"
          key={day}
          id={"member-config-"+day}
        >
          {day.substring(0, 3)}
        </ToggleButton>
      )
    })
  }

  const [workWeek, setWorkWeek] = useState(
    (() => {
      if (member) {
        let arr = []
        Object.entries(member.workWeek).map((entry) => {
          if (entry[1]) {
            arr.push(entry[0])
          }
          return null
        })
        return arr
      } else {
        return [days[0], days[1], days[2], days[3], days[4]]
      }
    })()
  )

  const checkOutlookLink = (link) => {
    let correctLink = true
    if(link && !link.includes('.ics')){
      dispatch(message.warning(t("mustBeIcsLink")))
      correctLink = false
    }
    if(link && link.length === 0){
      correctLink = true
    }
    setOutlookLink(link)
    return correctLink
  }

  const isPasswordValid = (password) => {
    // Define all password requirements and their respective regex patterns
    const requirements = [
      { name: t("atLeast8Characters"), pattern: /^.{8,}$/ },
      { name: t("atLeast1LowercaseLetter"), pattern: /[a-z]/ },
      { name: t("atLeast1UppercaseLetter"), pattern: /[A-Z]/ },
      { name: t("atLeast1Number"), pattern: /\d/ },
    ]

    // SPEC-057 L2: an empty password is always valid — creation sends none (welcome mail), and
    // on edit it means "leave unchanged".
    if (!password) {
      return { valid: true, missingRequirements: [] }
    }

    let missingRequirements = []

    // Check each requirement
    for (let requirement of requirements) {
      if (!requirement.pattern.test(password)) {
        missingRequirements.push(requirement.name)
      }
    }

    // Return validity and list of missing requirements
    return {
      valid: missingRequirements.length === 0,
      missingRequirements: missingRequirements,
    }
  }

  const disabled = generalFieldsReadOnly(member, authState)
  // SPEC-057 L2: which credential surface this form shows — self-service (own password/email),
  // or the "send reset mail" help button on someone else's profile.
  const isSelf = !!member && member.id === authState.memberId
  // Credentials are governed by IDENTITY, not by the general read-only flag above. A view-only
  // user — and a qa-manager holder with the member planning role — may change their OWN password
  // even though every planning field is read-only for them.
  const canEditOwnCredentials = isSelf && !member.archived
  // SPEC-057 L1: whether the CURRENT user can change QC capabilities on THIS member — used to
  // keep Save/Cancel rendered for a qa-manager holder whose general fields are all read-only.
  const capabilityEditable =
    showCapabilitySection && !member.archived && !authState.isViewOnly && assignableCapabilities.length > 0
  return (
    <>
      {esigOpen && member && (
        <ESignatureModal
          title={t("limsEsigTitle")}
          onSign={(sig) =>
            // Return the promise: the signature box stays open with an inline error on a
            // backend 403 (wrong password / not authorised to administer capabilities) and
            // closes only on success. Refresh the baseline so the form is clean again.
            runEditSave(pendingRes, sig).then(() => {
              setEsigOpen(false)
              setInitialCapabilities(qcCapabilities)
            })
          }
          onCancel={() => setEsigOpen(false)}
        />
      )}
    <ConfigContainer
      disabled={disabled}
      canSaveWhenDisabled={capabilityEditable || canEditOwnCredentials}
      canCancelWhenDisabled={capabilityEditable || canEditOwnCredentials}
      title={member ? t("editMember") : t("addMember")}
      submitHandler={() => {
        const passwordResult = isPasswordValid(password)
        if(!passwordResult.valid) {
          const passwordMessage = t("missingPasswordRequirements", { requirements: passwordResult.missingRequirements.join(', ') })
          return dispatch(message.warning(passwordMessage))
        }
        // SPEC-057 L2: a new password needs the current one for the backend re-auth.
        if (password && !currentPassword) {
          return dispatch(message.warning(t("currentPasswordRequired")))
        }
        const normalizedPlannertechLanguage = PLANNERTECH_LANGUAGE_CODES.includes(plannertechLanguage)
          ? plannertechLanguage
          : ""
        const currentPlannertechLanguage = member
          ? (PLANNERTECH_LANGUAGE_CODES.includes(member.plannertechLanguage) ? member.plannertechLanguage : "en")
          : ""
        const res = {
          initials,
          workHours: {
            min: startTime.as("seconds"),
            max: endTime.as("seconds"),
          },
          workWeek: (() => {
            let obj = {}
            days.map((day) => {
              obj[day] = false
              return null
            })
            workWeek.map((day) => {
              obj[day] = true
              return null
            })
            return obj
          })(),
          role,
          showOnPlan: role === "VIEW_ONLY" ? false : showOnPlan,
          outlookLink: (showOnPlan && role !== "VIEW_ONLY") ? (outlookLink || null) : null,
        }
        if (!member) {
          // Email is only sent on creation. SPEC-057 L2: no password — the new member gets a
          // welcome mail with a code and picks their own first password on the login screen.
          res.email = email
        }
        if (!member && normalizedPlannertechLanguage) {
          res.plannertechLanguage = normalizedPlannertechLanguage
        }
        if (member && normalizedPlannertechLanguage && normalizedPlannertechLanguage !== currentPlannertechLanguage) {
          res.plannertechLanguage = normalizedPlannertechLanguage
        }
        // Only use this if the user is exists
        if (!checkOutlookLink(outlookLink)){
          dispatch(message.warning(t("invalidOutlookLink")))
        }
        else if (member) {
          // A QC-role change is an authorisation change: require the e-signature FIRST and
          // only commit (and show success) once it is signed. Otherwise save directly.
          if (showLims && capabilitiesChanged()) {
            setPendingRes(res)
            setEsigOpen(true)
          } else {
            runEditSave(res, null).then(onClose)
          }
        } else {
          // SPEC-021 (B3): a NEW member is created WITHOUT QC capabilities — granting them is an
          // e-signed qa-manager act performed afterwards via Edit member (the capability section
          // is hidden on create).
          if (adminTeamId) {
            dispatch(members.createAsAdmin({ ...res, teamId: adminTeamId })).then(() => {
              // Fetching customers in different ways, depending on if user is customer-admin or admin for a company
              if(isCustomerAdmin) {
                dispatch(customerAdministration.fetchCustomers())
              } else {
                dispatch(seatInfo.fetch())
              }
            }).then(onClose)
          } else {
            dispatch(members.create(res)).then(onClose)
          }
        }
      }}
      cancelHandler={onClose}
    >
      <Container>
        <Title>{t("initials")}</Title>
        <Text required disabled={disabled} value={initials} textTransform={"uppercase"} onChange={(value) => setInitials(value)} />
      </Container>
      <Container>
        <Title>{t("emailLabel")}</Title>
        <Text
          required
          type="email"
          disabled={disabled || !!member}
          value={email}
          onValidate={(value) => {
            if (value.includes("..")) {
              return t("emailCannotContainDoubleDots")
            }
          }}
          textTransform={"lowercase"}
          onChange={(value) => setEmail(value)}
        />
        {/* SPEC-057 L2: the email is the identity anchor — only the member themselves can move it
            (password + code to the NEW address). #4125: the link belongs ON this row, not on a
            line of its own underneath it. */}
        {canEditOwnCredentials && !emailChangeOpen && (
          <Button
            variant="link"
            type="button"
            className="MemberConfig-email-change-toggle"
            onClick={() => setEmailChangeOpen(true)}
          >
            {t("changeEmail")}
          </Button>
        )}
      </Container>
      {canEditOwnCredentials && emailChangeOpen && (
        <EmailChangeSection onClose={() => setEmailChangeOpen(false)} />
      )}
      {/* SPEC-057 L2: nobody ever types another person's password. Self-service change below;
          helping a colleague = the reset-mail button (the server picks welcome vs. reset). */}
      {canEditOwnCredentials && (
        <>
          <Container>
            <Title>{t("currentPassword")}</Title>
            <Text type="password" value={currentPassword} onChange={setCurrentPassword} />
          </Container>
          <Container>
            <Title>{t("newPassword")}</Title>
            <Text type="password" value={password} onChange={setPassword} />
          </Container>
        </>
      )}
      {member && !isSelf && !disabled && (
        <Container>
          <Title>{t("password")}</Title>
          <Button
            variant="outline-primary"
            type="button"
            className="MemberConfig-send-reset-mail"
            onClick={() => dispatch(members.sendPasswordMail(member.id)).catch(() => {})}
          >
            {t("sendResetMail")}
          </Button>
        </Container>
      )}
      <Container>
        <Title>{t("plannertechLanguage")}</Title>
        <Select
          isMulti={false}
          required={!!member}
          disabled={disabled}
          collection={PLANNERTECH_LANGUAGE_OPTIONS}
          value={plannertechLanguage}
          onChange={(values) => setPlannertechLanguage(values[0] || "")}
          includeNone={!member}
          noneLabel={t("default")}
        />
      </Container>
      <Container>
        <Title>{t("role")}</Title>
        <ToggleButtonGroup
          type="radio"
          className="Memberconfig-toggleButtonGroup"
          name="role"
          defaultValue={member ? member.role : "MEMBER"}
          onChange={setRole}
        >
          <ToggleButton
            disabled={disabled || authState.isMember || authState.isViewOnly}
            value="MEMBER"
            variant="light"
            id="memberconfig-ismember_id"
            className="Memberconfig-radioButton"
            >
            {t("member")}
          </ToggleButton>
          <ToggleButton
            disabled={disabled || authState.isMember || authState.isViewOnly}
            value="PLANNER"
            variant="light"
            id="memberconfig-isplanner_id"
            className="Memberconfig-radioButton"
          >
            {t("planner")}
          </ToggleButton>
          <ToggleButton
            disabled={disabled || authState.isMember || authState.isViewOnly}
            value="VIEW_ONLY"
            variant="light"
            id="memberconfig-isviewonly_id"
            className="Memberconfig-radioButton"
          >
            {t("viewOnly")}
          </ToggleButton>
        </ToggleButtonGroup>
        {!member && authState.isMember && isQaManagerHolder && (
          // SPEC-057 L1: a qa-manager holder creates members with the MEMBER role only — the
          // toggles above are read-only for them, so say why the role is locked.
          <p className="MemberConfig-role-locked-hint">{t("limsQaManagerRoleLocked")}</p>
        )}
      </Container>
      {showCapabilitySection && (
        <Container>
          <Title>{t("limsQcCapabilities")}</Title>
          <Select
            collection={qcCapabilityOptions}
            value={qcCapabilities}
            onChange={handleCapabilityChange}
            // Deliberately NOT the general `disabled`: a qa-manager holder with the MEMBER
            // planning role edits capabilities here even though every other field is
            // read-only for them (SPEC-057 L1). An empty allow-set renders read-only.
            disabled={member.archived || authState.isViewOnly || assignableCapabilities.length === 0}
            menuPlacement="bottom"
          />
        </Container>
      )}
      {!onlyLibrarian && (
      <div>
      <Container>
        <Title>{t("showOnPlan")}</Title>
        <ToggleButtonGroup
          name="showOnPlan"
          className="Memberconfig-toggleButtonGroup"
          type="radio"
          value={role === "VIEW_ONLY" ? false : showOnPlan}
          onChange={(value) => {
            if (!value) setOutlookLink(null)
            setShowOnPlan(value)
          }}
        >
          <ToggleButton disabled={disabled || role === "VIEW_ONLY"} value={true} variant="light" id="memberconfig-showonplan_id" className="Memberconfig-radioButton">
            {t("shown")}
          </ToggleButton>
          <ToggleButton disabled={disabled || role === "VIEW_ONLY"} value={false} variant="light" id="memberconfig-hideonplan_id" className="Memberconfig-radioButton">
            {t("hidden")}
          </ToggleButton>
        </ToggleButtonGroup>
      </Container>
      <Container>
        <Title>{t("outlookLinkLabel")}</Title>
        <Text type="url" disabled={disabled || !showOnPlan || role === "VIEW_ONLY"} value={outlookLink} onChange={checkOutlookLink} />
      </Container>
      <div className="MemberConfig-availability">
        <p>{t("availability")}</p>
        <label>{t("workHours")}</label>
        <Container>
          <Title>{t("from")}</Title>
          <DurationInput disabled={disabled || !showOnPlan || role === "VIEW_ONLY"} value={startTime} onChange={setStartTime} required isTime />
          <Title>{t("to")}</Title>
          <DurationInput disabled={disabled || !showOnPlan || role === "VIEW_ONLY"} value={endTime} onChange={setEndTime} required isTime />
        </Container>
        <label>{t("workWeek")}</label>
        <div className="MemberConfig-work-week">
          <Container>
            <ToggleButtonGroup
              variant="light"
              type="checkbox"
              value={workWeek}
              onChange={(e) => {
                setWorkWeek(e)
              }}
            >
              {renderDays(disabled || !showOnPlan || role === "VIEW_ONLY")}
            </ToggleButtonGroup>
          </Container>
        </div>
      </div>
      </div>
      )}
    </ConfigContainer>
    </>
  )
}
